import { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'components/DashboardNavbar';
import Footer from 'examples/Footer';

import DateFilterPresets from 'components/DateFilterPresets';
import EntityFilter from 'components/EntityFilter';
import MetricCard from 'components/MetricCard';
import usePersistedFilters from 'hooks/usePersistedFilters';
import { useNavigate } from 'react-router-dom';

import { getStatistics, ggrForPeriod, getPvPMetrics } from 'services/analytics';
import { exportDashboardToCsv } from 'utils/exportToCsv';

function PvPDashboard() {
  const navigate = useNavigate();
  const { filters, setDateRange, setEntityFilters } = usePersistedFilters();

  const [statistics, setStatistics] = useState(null);
  const [ggr, setGgr] = useState(null);
  const [pvpMetrics, setPvpMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const clientNames = filters.clientNames || [];

      // Build filter params for ALL APIs - use first ID from arrays for single-select APIs
      const apiFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        clientId: filters.clientIds?.[0] || undefined,
        operatorId: filters.operatorIds?.[0] || undefined,
        brandId: filters.brandIds?.[0] || undefined,
        gameCategory: 'pvp'
      };

      const [statsRes, ggrRes, pvpRes] = await Promise.all([
        getStatistics(apiFilters),
        ggrForPeriod(apiFilters),
        getPvPMetrics(apiFilters)
      ]);

      let ggrData = ggrRes?.data || [];

      if (clientNames.length > 0 && Array.isArray(ggrData)) {
        ggrData = ggrData.filter((g) => clientNames.includes(g.client));
      }

      setStatistics(statsRes?.data || {});
      setGgr(ggrData);
      setPvpMetrics(pvpRes?.success ? pvpRes.data : null);
    } catch (err) {
      console.error('Error fetching PvP dashboard data:', err);
    }
    setLoading(false);
  }, [
    filters.startDate,
    filters.endDate,
    filters.clientNames,
    filters.clientIds,
    filters.operatorIds,
    filters.brandIds
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange = (startDate, endDate, preset) => {
    setDateRange(startDate, endDate, preset);
  };

  const handleEntityFilterChange = (entityFilters) => {
    setEntityFilters(entityFilters);
  };

  const getGgrTotal = () => {
    if (!ggr || !Array.isArray(ggr) || ggr.length === 0) return '-';
    const total = ggr.reduce((sum, g) => sum + (parseFloat(g.ggr) || 0), 0);
    return total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getGgrBreakdown = () => {
    if (!ggr || !Array.isArray(ggr) || ggr.length === 0) return null;
    return [...ggr]
      .sort((a, b) => (parseFloat(b.ggr) || 0) - (parseFloat(a.ggr) || 0))
      .map((g) => ({
        label: g.client || g.clientName || 'Unknown',
        value: parseFloat(g.ggr).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      }));
  };

  const getBetsBreakdown = () => {
    if (!statistics?.betsForPeriodAmount) return null;
    return statistics.betsForPeriodAmount.map((b) => ({
      label: b.currency,
      value: parseFloat(b.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }));
  };

  // PvP metrics helpers
  const getPvpGgr = () => {
    if (pvpMetrics?.ggr !== undefined) {
      return pvpMetrics.ggr.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return getGgrTotal();
  };

  const getPvpNgr = () => {
    if (pvpMetrics?.ngr !== undefined) {
      return pvpMetrics.ngr.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return null;
  };

  const getPvpTotalBets = () => {
    if (pvpMetrics?.totalBets) {
      return {
        count: pvpMetrics.totalBets.count.toLocaleString(),
        amount: pvpMetrics.totalBets.amount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      };
    }
    return null;
  };

  const getPvpTotalWins = () => {
    if (pvpMetrics?.totalWins) {
      return {
        count: pvpMetrics.totalWins.count.toLocaleString(),
        amount: pvpMetrics.totalWins.amount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      };
    }
    return null;
  };

  const getPvpRoundsPlayed = () => {
    if (pvpMetrics?.roundsPlayed !== undefined) {
      return pvpMetrics.roundsPlayed.toLocaleString();
    }
    return null;
  };

  const getPvpAvgBetSize = () => {
    if (pvpMetrics?.averageBetSize !== undefined) {
      return pvpMetrics.averageBetSize.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return null;
  };

  const getPvpUniquePlayers = () => {
    if (pvpMetrics?.uniquePlayers !== undefined) {
      return pvpMetrics.uniquePlayers.toLocaleString();
    }
    return null;
  };

  const getPvpTotalSessions = () => {
    if (pvpMetrics?.totalSessions !== undefined) {
      return pvpMetrics.totalSessions.toLocaleString();
    }
    return null;
  };

  const getPvpRolledBackBets = () => {
    if (pvpMetrics?.rolledBackBets) {
      return {
        count: pvpMetrics.rolledBackBets.count.toLocaleString(),
        amount: pvpMetrics.rolledBackBets.amount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }),
        percentage: pvpMetrics.rolledBackBets.percentageOfTotal?.toFixed(2) || '0.00'
      };
    }
    return null;
  };

  const getPvpRakeCollected = () => {
    if (pvpMetrics?.rakeCollected !== undefined) {
      return pvpMetrics.rakeCollected.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return null;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={{ xs: 1, md: 3 }}>
        <MDBox mb={3}>
          <Card>
            {/* Red accent line */}
            <MDBox
              sx={{
                height: '3px',
                background: 'linear-gradient(90deg, #e2001b, #ff4f69, transparent 80%)',
                borderRadius: '12px 12px 0 0'
              }}
            />
            <MDBox p={{ xs: 1.5, md: 2.5 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={1.5}>
                <MDTypography variant='h5' fontWeight='bold'>
                  PvP Games Dashboard
                </MDTypography>
                <MDBox display='flex' gap={1}>
                  <MDButton
                    variant='outlined'
                    color='success'
                    size='small'
                    onClick={() => exportDashboardToCsv('pvp-dashboard-export', { ggr, statistics, filters })}
                    disabled={loading}
                  >
                    <Icon sx={{ mr: 0.5 }}>download</Icon>
                    Export CSV
                  </MDButton>
                  <MDButton variant='outlined' color='info' size='small' onClick={fetchData}>
                    Refresh
                  </MDButton>
                  <MDButton
                    variant='gradient'
                    color='dark'
                    size='small'
                    onClick={() => navigate('/multiplayer-liquidity')}
                  >
                    Liquidity Report
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox mb={2}>
                <MDTypography variant='button' color='text' fontWeight='regular' mb={1}>
                  Date Range
                </MDTypography>
                <DateFilterPresets
                  onDateChange={handleDateChange}
                  preset={filters.preset || 'today'}
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                />
              </MDBox>

              <MDBox>
                <MDTypography variant='button' color='text' fontWeight='regular' mb={1}>
                  Entity Filters
                </MDTypography>
                <EntityFilter
                  onFilterChange={handleEntityFilterChange}
                  initialValues={{
                    clientIds: filters.clientIds,
                    operatorIds: filters.operatorIds,
                    brandIds: filters.brandIds
                  }}
                />
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>

        <MDBox mb={3}>
          <MDTypography variant='h6' fontWeight='medium' mb={2}>
            Core Financial Metrics
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='GGR'
                value={getPvpGgr()}
                subtitle='Gross Gaming Revenue'
                icon='attach_money'
                iconColor='success'
                loading={loading}
                breakdown={getGgrBreakdown()}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='NGR'
                value={getPvpNgr()}
                placeholder={!getPvpNgr()}
                placeholderText='No data'
                subtitle='Net Gaming Revenue'
                icon='trending_up'
                iconColor='info'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Total Bets'
                value={getPvpTotalBets()?.count || statistics?.betsForPeriod?.toLocaleString() || '-'}
                subtitle={getPvpTotalBets() ? `$${getPvpTotalBets().amount} turnover` : 'Count'}
                icon='casino'
                iconColor='primary'
                loading={loading}
                breakdown={getBetsBreakdown()}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Total Wins'
                value={getPvpTotalWins()?.count}
                placeholder={!getPvpTotalWins()}
                placeholderText='No data'
                subtitle={getPvpTotalWins() ? `$${getPvpTotalWins().amount} payout` : 'Player Winnings'}
                icon='emoji_events'
                iconColor='warning'
                loading={loading}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mb={3}>
          <MDTypography variant='h6' fontWeight='medium' mb={2}>
            Activity & Engagement Metrics
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Rounds Played'
                value={getPvpRoundsPlayed()}
                placeholder={!getPvpRoundsPlayed()}
                placeholderText='No data'
                icon='sports_esports'
                iconColor='info'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Average Bet Size'
                value={getPvpAvgBetSize() ? `$${getPvpAvgBetSize()}` : null}
                placeholder={!getPvpAvgBetSize()}
                placeholderText='No data'
                icon='payments'
                iconColor='secondary'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Unique Players'
                value={getPvpUniquePlayers()}
                placeholder={!getPvpUniquePlayers()}
                placeholderText='No data'
                icon='people'
                iconColor='info'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetricCard
                title='Total Sessions'
                value={getPvpTotalSessions()}
                placeholder={!getPvpTotalSessions()}
                placeholderText='No data'
                icon='event'
                iconColor='primary'
                loading={loading}
              />
            </Grid>
          </Grid>
        </MDBox>

        <MDBox mb={3}>
          <MDTypography variant='h6' fontWeight='medium' mb={2}>
            PvP-Specific Metrics
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Rake Collected'
                value={getPvpRakeCollected() ? `$${getPvpRakeCollected()}` : null}
                placeholder={!getPvpRakeCollected()}
                placeholderText='No data'
                subtitle='Total rake'
                icon='account_balance_wallet'
                iconColor='success'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Non-Rollbacked Bets'
                value={
                  pvpMetrics?.totalBets?.count?.toLocaleString() ||
                  statistics?.nonRollbackedBetsForPeriod?.toLocaleString() ||
                  '-'
                }
                subtitle='Count'
                icon='check_circle'
                iconColor='success'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Rolled Back Bets'
                value={getPvpRolledBackBets()?.count}
                placeholder={!getPvpRolledBackBets()}
                placeholderText='No data'
                subtitle={getPvpRolledBackBets() ? `${getPvpRolledBackBets().percentage}% of total` : 'Count'}
                icon='cancel'
                iconColor='error'
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title='Rolled Back Amount'
                value={getPvpRolledBackBets() ? `$${getPvpRolledBackBets().amount}` : null}
                placeholder={!getPvpRolledBackBets()}
                placeholderText='No data'
                subtitle='Total rolled back'
                icon='money_off'
                iconColor='error'
                loading={loading}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PvPDashboard;
